import TabletGuard from '@/router/guards/tablet-guard'
import { PACKAGE_PERMISSIONS } from '@/constants/features'
import multiGuard from '@/router/utils/vue-router-multiguard'
import PackagePermissionsGuard from '@/router/guards/package-permissions-guard'
import productReservationAddonGuard from '@/router/guards/product-reservation-addon-guard'
import SUBUSER_PERMISSION_SLUGS from '@/constants/authorization/subuser-permission-slugs'

export default [
  {
    path: 'customers',
    name: 'admin:customers',
    component: () => import(/* webpackChunkName: "clients" */ '@/views/admin/marketing/clients/ClientsTable.vue'),
  },
  {
    path: 'queues',
    name: 'admin:queues',
    alias: '/admin/queue',
    beforeEnter: multiGuard([TabletGuard, PackagePermissionsGuard]),
    meta: { packagePermissions: [PACKAGE_PERMISSIONS.QUEUE] },
    component: () => import(/* webpackChunkName: "queues" */ '@/views/admin/management/queues/QueuesWizard.vue'),
  },
  {
    path: 'reservations',
    name: 'admin:reservations',
    beforeEnter: multiGuard([PackagePermissionsGuard]),
    meta: { packagePermissions: [PACKAGE_PERMISSIONS.RESERVATION] },
    component: () => import(/* webpackChunkName: "reservations" */ '@/views/admin/management/reservations/ReservationsWizard.vue'),
  },
  {
    path: 'product-reservations',
    name: 'admin:product-reservations',
    beforeEnter: multiGuard([productReservationAddonGuard, PackagePermissionsGuard]),
    meta: {
      packagePermissions: [PACKAGE_PERMISSIONS.PRODUCT_RESERVATION],
      permission: SUBUSER_PERMISSION_SLUGS.ORDERS_RESERVATION_ORDERS_VIEW,
    },
    component: () => import(/* webpackChunkName: "product-reservations" */ '@/views/admin/management/reservations/ProductReservationsWizard.vue'),
  },
  {
    path: 'orders',
    name: 'admin:orders',
    alias: '/admin/orders',
    meta: { permission: SUBUSER_PERMISSION_SLUGS.ORDERS_VIEW },
    component: () => import(/* webpackChunkName: "reservations" */ '@/views/admin/management/orders/OrdersWizard.vue'),
  },
  {
    path: 'abandoned-orders',
    name: 'admin:abandoned-orders',
    alias: '/admin/abandoned-orders',
    meta: { permission: SUBUSER_PERMISSION_SLUGS.ORDERS_ABANDONED_VIEW },
    component: () => import(/* webpackChunkName: "reservations" */ '@/views/admin/management/orders/AbandonedOrdersWizard.vue'),
  },
  {
    path: 'orders/new-order',
    name: 'admin:new-order',
    alias: '/admin/orders/new-orders',
    meta: { permission: SUBUSER_PERMISSION_SLUGS.ORDERS_MANAGE },
    component: () => import(/* webpackChunkName: "reservations" */ '@/views/admin/management/orders/AddOrder.vue'),
  },
  {
    path: 'orders/:id/edit',
    name: 'admin:edit-order',
    alias: '/admin/orders/:id/edit',
    meta: { permission: SUBUSER_PERMISSION_SLUGS.ORDERS_MANAGE },
    component: () => import(/* webpackChunkName: "reservations" */ '@/views/admin/management/orders/EditOrder.vue'),
  },
  {
    path: 'returned-orders',
    name: 'admin:return-orders',
    alias: '/admin/return-orders',
    meta: { permission: SUBUSER_PERMISSION_SLUGS.ORDERS_RETURN_ORDERS_VIEW },
    component: () => import(/* webpackChunkName: "reservations" */ '@/views/admin/management/return-orders/ReturnOrdersWizard.vue'),
  },
  {
    path: 'refunds',
    name: 'admin:refunds',
    alias: '/admin/refunds',
    meta: { permission: SUBUSER_PERMISSION_SLUGS.ORDERS_REFUND },
    component: () => import(/* webpackChunkName: "refunds" */ '@/views/admin/management/refunds/RefundsWizard.vue'),
  },
  {
    path: 'orders/:id/history',
    name: 'admin:orders-histories',
    alias: '/admin/orders/:id/history',
    meta: { permission: SUBUSER_PERMISSION_SLUGS.ORDERS_VIEW },
    component: () => import(/* webpackChunkName: "reservations" */ '@/views/admin/management/orders/OrderHistories.vue'),
  },
  {
    path: 'orders/invoice/:id',
    name: 'admin:order-invoice',
    meta: { permission: SUBUSER_PERMISSION_SLUGS.ORDERS_VIEW },
    component: () => import(/* webpackChunkName: "reservations" */ '@/views/admin/management/orders/components/OrderInvoice.vue'),
  },
  {
    path: 'transactions',
    name: 'admin:transactions',
    alias: '/admin/transactions',
    component: () => import(/* webpackChunkName: "reservations" */ '@/views/admin/management/transactions/TransactionsTabs.vue'),
  },
  {
    path: 'order-display-system',
    name: 'admin:kitchen',
    beforeEnter: multiGuard([PackagePermissionsGuard]),
    meta: {
      packagePermissions: [PACKAGE_PERMISSIONS.KITCHEN],
      permission: [
        SUBUSER_PERMISSION_SLUGS.ORDER_DISPLAY_VIEW,
        SUBUSER_PERMISSION_SLUGS.ORDER_DISPLAY_MANAGE,
      ],
    },
    component: () => import(/* webpackChunkName: "kitchen" */ '@/views/admin/management/order-display-system/OrderDisplaySystemWizard.vue'),
  },
  {
    path: 'statistics',
    name: 'admin:statistics',
    beforeEnter: multiGuard([PackagePermissionsGuard]),
    meta: { packagePermissions: [PACKAGE_PERMISSIONS.STATISTICS] },
    component: () => import(/* webpackChunkName: "statistics" */ '@/views/admin/management/statistics/StatisticsView.vue'),
  },
  {
    path: 'branches-statistics',
    name: 'admin:branches-statistics',
    beforeEnter: multiGuard([PackagePermissionsGuard]),
    meta: { packagePermissions: [PACKAGE_PERMISSIONS.STATISTICS] },
    component: () => import(/* webpackChunkName: "statistics" */ '@/views/admin/management/statistics/BranchesStatisticsView.vue'),
  },
  {
    path: 'store',
    name: 'admin:store',
    component: () => import(/* webpackChunkName: "integrations" */ '@/views/admin/integration-views/IntegrationsList.vue'),
  },
  {
    path: 'integrations/:id',
    name: 'admin:integrations:view',
    component: () => import(/* webpackChunkName: "integrations" */ '@/views/admin/integration-views/IntegrationView.vue'),
  },
  {
    path: 'addons',
    name: 'admin:addons',
    component: () => import(/* webpackChunkName: "addons" */ '@/views/admin/addons-views/AddonsList.vue'),
  },
  {
    path: 'addons/:id',
    name: 'admin:addons:view',
    component: () => import(/* webpackChunkName: "addons" */ '@/views/admin/addons-views/AddonView.vue'),
  },
]
